import type { FieldType, ICollection, IQuoteLine, IQuoteTextLine } from '@/interfaces'
import { Collection, QuoteTextLine } from '@/models'

import { addToJSON } from './helper'

export class QuoteLine implements IQuoteLine {
	[key: string]: FieldType
	lineId: number
	quoteId: number | string
	lineNum: number
	item: string
	itemCode?: string
	rev: string
	description: string
	quantity?: number
	cost?: number
	price: number
	isAlternative: boolean
	isDeleted: boolean
	isTax?: boolean

	// Text sub-lines
	textLines: ICollection<IQuoteTextLine>

	// Required for IRecord
	id: number | string

	constructor({
		lineId,
		id,
		quoteId,
		lineNum,
		item,
		itemCode,
		rev,
		description,
		itemDescription, // legacy field
		quantity,
		cost,
		lineCost, // legacy field
		estimatedCost, // legacy field
		internalCost, // legacy field
		price,
		unitPrice, // legacy field
		lineTotal, // legacy field
		isAlternative,
		isDeleted,
		isTax,
		textLines
	}: Partial<IQuoteLine> = {}) {
		this.lineId = lineId || 0
		this.id = id || lineId || 0
		this.quoteId = quoteId || 0
		this.lineNum = lineNum !== undefined ? lineNum : 1
		this.item = item || ''
		this.itemCode = itemCode
		this.rev = rev || ''
		this.description = String(description || itemDescription || '')
		this.quantity = quantity || 1
		this.cost = Number(lineCost || estimatedCost || internalCost || cost || 0)
		this.price = Number(lineTotal || unitPrice || price || 0)
		this.isAlternative = isAlternative || false
		this.isDeleted = isDeleted || false

		// Only set isTax if it's provided or if itemCode indicates it's a tax line
		if (isTax !== undefined) {
			this.isTax = isTax
		} else if (this.itemCode && this.itemCode.toLowerCase() === 'salestax') {
			this.isTax = true
		}
		// Don't set isTax at all if it's not a tax line - this preserves the original data structure

		// Text sub-lines
		this.textLines = new Collection<IQuoteTextLine>(QuoteTextLine, textLines || [])

		addToJSON(QuoteLine)
	}

	public addTextLine(text: string): IQuoteTextLine {
		const nextLineNum = Math.max(0, ...this.textLines.map((t) => t.lineNum || 0)) + 1
		return this.textLines.add({
			textLineId: 0,
			quoteLineId: this.lineId,
			lineNum: nextLineNum,
			text: text,
			afterLineNumber: 0,
			isDeleted: false
		} as IQuoteTextLine)
	}

	public removeTextLine(textLineId: number): void {
		const textLine = this.textLines.find((t) => t.textLineId === textLineId)
		if (textLine) {
			textLine.isDeleted = true
		}
	}

	public getActiveTextLines(): IQuoteTextLine[] {
		return this.textLines.filter((t) => !t.isDeleted)
	}

	public calculateExtendedPrice(quantity: number = 1): number {
		return this.price * quantity
	}

	public hasMargin(): boolean {
		return this.cost !== undefined && this.cost > 0 && this.price > 0
	}

	public getMargin(): number {
		if (!this.hasMargin()) return 0
		return this.price - (this.cost || 0)
	}

	public getMarginPercentage(): number {
		if (!this.hasMargin() || this.price === 0) return 0
		return ((this.price - (this.cost || 0)) / this.price) * 100
	}
}
